import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CART_STATUS, Cart, CartOffer, Currency, Offer, User } from 'src/app/models/verylys';
import { AbstractService, Pagination, PaginationResult, PopulateRequest } from '../abstract/abstract.service';
import { VlApiService } from '../vl-api/vl-api.service';
import { LOCALSTORAGE_KEYS } from 'src/config';
import { SocketIOService } from '../socketio/socketio.service';
import { finalize } from 'rxjs/operators';

export enum PAYMENT_METHODS {
    PAYPAL,
    PAYSAFCARD,
    STRIPE,
    VIRTUAL_CURRENCY,
    FREE_DEVELOPEMENT
}

export interface StripePaymentType {
    stripe?: {
        type: string
    }
    name: string,
    id: string,
    fingerprint?: boolean;
    description?: string;
    companyPaymentType?: string;
}

export interface VirtualCurrencyPaymentType {
    name: string,
    id: string,
    companyPaymentType?: string;
}

export interface PaypalPaymentType {
    id?: string;
    name?: string;
    fingerprint?: boolean;
    fingerprintId: string;
    description?: string;
    companyPaymentType?: string;
}

export type PaymentType = PaypalPaymentType | StripePaymentType | VirtualCurrencyPaymentType;

export interface CartCheckoutResult {
    _id: string;
    development_provider: {
        company_payment_type: string;
    },
    stripe?: Map<string, { company_payment_type: string }>[];
    virtual_currencies?: Map<string, { _id: string, company_payment_type: string }>[],
    paypal?: {
        value: boolean;
        company_payment_type: string;
    },
    payment_fingerprints?: {
        type: string,
        id: string,
        parameters: {
            exp_year: string;
            last4: string;
            type: string;
            paypal_payer_email: string;
        }
    }[];
}

export type PaymentContext = StripePaymentContext | PaypalPaymentContext;

export type StripePaymentContext = string;
export type PaypalPaymentContext = string;

enum PAY_TYPE {
    VIRTUAL_CURRENCY = "Virtual_currency",
    PAYMENT_FINGERPRINT = "Payment_fingerprint",
    STRIPE = "stripe",
    PAYPAL = "paypal"
}

type CalculatedPriceItem = { amount: number, format: string };

export interface CartCalculatedPriceResult {
    total: CalculatedPriceItem;
    total_no_promotion: CalculatedPriceItem;
    taxes: CalculatedPriceItem;
    sub_total: CalculatedPriceItem;
}

@Injectable({
    providedIn: 'root'
})
export class CartService extends AbstractService<Cart> {
    route = "/carts/";
    resourceReferenceLabel = "Cart";

    protected crudMethods = {
        create: false,
        update: false,
        delete: false,
        get: false,
        getAll: false
    }

    private onGettingMyCart$: Subject<void> = new Subject();
    get onGettingMyCart(): Observable<void> {
        return this.onGettingMyCart$.asObservable();
    }

    public isValidCheckout(result: CartCheckoutResult): boolean {
        if (!result) {
            return false;
        }

        if (result.stripe) {
            return true;
        }

        if (result.payment_fingerprints?.length > 0) {
            return true;
        }

        if (result.paypal?.value) {
            return true;
        }

        if (result.development_provider != null) {
            return true;
        }

        return false;
    }

    public getCheckedOutCarts() {
        return this.vlapi.get<PaginationResult<Cart>>(`${this.route}checked-out`);
    }

    /** Get the cart of a session */
    public getOrCreateMyCart(populateRequest?: PopulateRequest): Observable<Cart> {
        const populate: PopulateRequest = {
            ...populateRequest,
            currencyId: true,
            countryId: true
        };

        return this.vlapi.get<Cart>(`${this.route}get-or-create-my-cart/`, { populateRequest: populate })
            .pipe(
                finalize(() => this.onGettingMyCart$.next())
            );
    }

    /**
    * Refresh a cart offer on the cart
    * 
    * @param cartOffer       CartOffer
   */
    public refresh(/*cartOffer: CartOffer, */offer: Offer) {
        let body: any = {
            offerId: offer._id
        }

        /* if (cartOffer?._id) {
             body.cartOfferId = cartOffer._id;
         } */

        return this.vlapi.post<CartOffer>(`${this.route}refresh-offer`, body);
    }

    /** Checkout a cart */
    public checkout() {
        return this.vlapi.post<CartCheckoutResult>(`${this.route}checkout`);
    }

    /** Checkout a cart */
    public flush() {
        return this.vlapi.delete<string>(`${this.route}flush`);
    }

    /** Get the price of a cart */
    public calculatedPrice(cart: Cart) {
        return this.vlapi.get<CartCalculatedPriceResult>(`${this.route}${cart._id}/calculated-price`);
    }

    public requestPaymentContext(cartId: string, paymentTypeObject: PaymentType) {
        return this.vlapi.post<PaymentContext>(`${this.route}${cartId}/request-payment-context`, {
            paymentTypeObject: paymentTypeObject
        })
    }

    public payWithFingerprint(cartId: string, fingerprintId: string) {
        return this.vlapi.post<string>(`${this.route}${cartId}/pay-with-fingerprint`, { fingerprintId: fingerprintId });
    }

    public payWithVirtualCurrency(cartId: string, virtualCurrencyId: string, companyPaymentType: string) {
        return this.vlapi.post<any>(`${this.route}${cartId}/pay`, {
            type: PAY_TYPE.VIRTUAL_CURRENCY,
            virtualCurrencyId: virtualCurrencyId,
            companyPaymentType: companyPaymentType
        });
    }

    public validate(value: Cart, ...args: any): void {

    }

    public normalizeStatus(cart: Cart): string {
        switch (cart?.parameters?.status?.value) {
            case CART_STATUS.CHECK_FRAUD:
                return $localize`:@@rental_status.check-fraud:Awaiting approval`;
            case CART_STATUS.DISPUTED:
                return $localize`:@@rental_status.disputed:Under contention`;
            case CART_STATUS.FAILURE:
                return $localize`:@@rental_status.check-failure:Failed`;
            case CART_STATUS.IN_PROGRESS:
                return $localize`:@@rental_status.in-progress:In progress`;
            case CART_STATUS.SUCCESS:
                return $localize`:@@rental_status.success:Completed`;
        }
    }
}